import { useTranslation } from 'react-i18next';

import { APP_LANGUAGES, type AppLanguage, readAppLanguage } from '../lib/useChangeLanguage';

/** Значения поля на всех языках, как их отдаёт бэкенд. */
export type LocalizedValues = {
  ru?: string | null;
  uz?: string | null;
  en?: string | null;
};

/**
 * Значение на нужном языке.
 *
 * Запасной порядок — ru → uz → en: у части позиций переводы заполнены не
 * везде, и пустая строка на карточке хуже, чем строка на соседнем языке.
 *
 * Раньше выбор был двухветочным (`lang === 'ru' ? title : title_uz`), и
 * английский молча попадал в узбекскую ветку: пользователь с `en` видел
 * вперемешку переведённый интерфейс и узбекские названия блюд.
 */
export const localized = (
  language: string | undefined,
  values: LocalizedValues
): string => {
  const lang = (APP_LANGUAGES as readonly string[]).includes(
    (language ?? '').slice(0, 2)
  )
    ? ((language ?? '').slice(0, 2) as AppLanguage)
    : 'ru';

  const order: AppLanguage[] =
    lang === 'ru' ? ['ru', 'uz', 'en'] : lang === 'uz' ? ['uz', 'ru', 'en'] : ['en', 'ru', 'uz'];

  for (const key of order) {
    const value = values[key];
    if (value) return value;
  }

  return '';
};

/**
 * Текущий язык интерфейса, от которого компонент перерисуется при
 * переключении.
 *
 * `localStorage.getItem('language')` в теле компонента этого не гарантирует:
 * значение прочитается заново только если что-то ещё вызовет рендер. Отсюда
 * и «случайный язык» — часть экрана обновлялась, часть оставалась на старом.
 */
export const useAppLanguage = (): AppLanguage => {
  const { i18n } = useTranslation();
  const current = i18n.resolvedLanguage ?? i18n.language;

  return (APP_LANGUAGES as readonly string[]).includes(current)
    ? (current as AppLanguage)
    : readAppLanguage();
};
